import React, { useEffect, useState } from "react";
import {
  LayoutDashboard, Building2, Compass, FileText, ChevronLeft, ChevronRight, X,
} from "lucide-react";

const STORAGE_KEY = "cesco_onboarding_v1_seen";

function seenKey(userId) {
  return userId ? `${STORAGE_KEY}:${userId}` : STORAGE_KEY;
}

export function hasSeenOnboarding(userId) {
  try { return !!localStorage.getItem(seenKey(userId)); } catch { return true; }
}

export function markOnboardingSeen(userId) {
  try { localStorage.setItem(seenKey(userId), "1"); } catch (e) { /* localStorage unavailable (private mode) - best-effort */ }
}

export function resetOnboarding(userId) {
  try { localStorage.removeItem(seenKey(userId)); } catch (e) { /* localStorage unavailable - best-effort */ }
}

const SLIDES = [
  {
    icon: LayoutDashboard,
    title: "Welcome to the Cesco CRM",
    body: (name) => (
      <>
        <p className="mb-3">
          G'day{name ? `, ${name.split(" ")[0]}` : ""} - this quick 60-second tour shows you
          the 4 things you'll use every day.
        </p>
        <p className="text-sm text-gray-600">
          You can re-open this guide any time via the <b>Help</b> link in the sidebar.
        </p>
      </>
    ),
  },
  {
    icon: LayoutDashboard,
    title: "1. Start your day on the Dashboard",
    body: () => (
      <>
        <p className="mb-2">Your dashboard shows:</p>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li><b>Open Quotes</b> + <b>Pipeline Value</b> in AUD</li>
          <li><b>Tasks Due Today</b> and <b>Overdue Tasks</b> tiles</li>
          <li><b>At-Risk Quotes</b> - open quotes past their expected purchase date</li>
          <li>Your <b>open task feed</b> - tick them off, or click <b>Complete</b> to log an outcome</li>
        </ul>
        <p className="mt-3 text-sm text-gray-600">
          Tip: every task has a <b>Type</b> (Phone Call, Email, Meeting…) so you can see how
          you're spending your day at a glance.
        </p>
      </>
    ),
  },
  {
    icon: Building2,
    title: "2. A company has 7 tabs",
    body: () => (
      <>
        <p className="mb-2">Click any company to open its profile. The tabs are:</p>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li><b>Details</b> - address, phone, Summary on Company</li>
          <li><b>Contacts</b> - people at this company (tap the phone # to call, email to compose)</li>
          <li><b>Specifics</b> - pick their product segments (Mixer / Drill / Other…)</li>
          <li><b>Discovery</b> - capture intel as you learn (tap a "topic not yet captured" chip to start)</li>
          <li><b>Equipment</b> - what kit they already own</li>
          <li><b>Activity</b> - every task, note, and system event, with pinned open tasks up top</li>
          <li><b>Quotes</b> - quote register for this company only</li>
        </ul>
        <p className="mt-3 text-sm text-gray-600">
          The floating <b>+ Add task</b> button is always visible - use it liberally.
        </p>
      </>
    ),
  },
  {
    icon: Compass,
    title: "3. Log intel in Discovery",
    body: () => (
      <>
        <p className="mb-2">
          The <b>Discovery</b> tab is how Cesco builds a map of each customer's world.
        </p>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li>See a pale chip like "+ The current tooling / parts / instrument spend per month"? Tap it - the topic is pre-filled.</li>
          <li>Paste what you learned from the call and hit <b>Save</b>.</li>
          <li>Need to fix a typo or update a figure later? Each entry has <b>Edit</b> and <b>Delete</b> buttons.</li>
        </ul>
        <p className="mt-3 text-sm text-gray-600">
          The more you log, the better the Pipeline picture for Justin and the whole team.
        </p>
      </>
    ),
  },
  {
    icon: FileText,
    title: "4. Build and chase quotes",
    body: () => (
      <>
        <p className="mb-2">Quotes live per-company and also in the global <b>Quotes</b> register.</p>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li>Open a company → <b>Quotes</b> tab → <b>New quote</b>.</li>
          <li>Set an <b>Expected purchase date</b> - it drives your At-Risk tile if it slips.</li>
          <li>Set a <b>Reminder date</b> - it'll show in your notification bell and daily digest.</li>
          <li>Status changes to <b>WON</b> or <b>LOST</b> require a reason - that's how we learn.</li>
        </ul>
        <p className="mt-3 text-sm text-gray-600">
          You're ready. Any questions, ask Justin or re-open this guide from the sidebar.
        </p>
      </>
    ),
  },
];

export default function OnboardingGuide({ userName = "", userId = null, onClose }) {
  const [i, setI] = useState(0);
  const last = i === SLIDES.length - 1;
  const Slide = SLIDES[i];

  function finish() {
    markOnboardingSeen(userId);
    onClose?.();
  }

  // ESC to close
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") finish();
      if (e.key === "ArrowRight" && !last) setI(x => x + 1);
      if (e.key === "ArrowLeft" && i > 0) setI(x => x - 1);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [i, last]);

  const Icon = Slide.icon;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
         data-testid="onboarding-overlay" onClick={finish}>
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden"
           onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[var(--theme-accent-50)] flex items-center justify-center">
              <Icon size={18} />
            </div>
            <div className="text-[11px] uppercase tracking-widest text-gray-500">
              Onboarding · {i + 1} / {SLIDES.length}
            </div>
          </div>
          <button className="text-gray-500 hover:text-black" onClick={finish}
                  data-testid="onboarding-close" aria-label="Close">
            <X size={18}/>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6 min-h-[260px]">
          <h2 className="text-2xl font-bold tracking-tight mb-3">{Slide.title}</h2>
          <div className="text-[15px] text-gray-800 leading-relaxed">
            {typeof Slide.body === "function" ? Slide.body(userName) : Slide.body}
          </div>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 pb-3">
          {SLIDES.map((slide, idx) => (
            <button key={slide.title}
              onClick={() => setI(idx)}
              className={`h-2 rounded-full transition-all ${idx === i ? "w-6 bg-black" : "w-2 bg-gray-300 hover:bg-gray-500"}`}
              data-testid={`onboarding-dot-${idx}`}
              aria-label={`Go to slide ${idx + 1}`}/>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t bg-gray-50">
          <button
            className="cesco-btn-ghost flex items-center gap-1 disabled:opacity-40"
            disabled={i === 0}
            onClick={() => setI(x => Math.max(0, x - 1))}
            data-testid="onboarding-prev">
            <ChevronLeft size={16}/> Back
          </button>
          <div className="flex items-center gap-2">
            {!last && (
              <button className="text-sm text-gray-500 hover:text-black" onClick={finish}
                      data-testid="onboarding-skip">Skip</button>
            )}
            {last ? (
              <button className="cesco-btn-primary" onClick={finish}
                      data-testid="onboarding-done">Got it - let's go</button>
            ) : (
              <button className="cesco-btn-primary flex items-center gap-1"
                      onClick={() => setI(x => Math.min(SLIDES.length - 1, x + 1))}
                      data-testid="onboarding-next">
                Next <ChevronRight size={16}/>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
